import { useNavigate, useParams } from "react-router";
import { Paper, Stack } from "@mui/material";
import { Filter, User, UserRole } from "../../../services/swagger";
import { useEffect, useState } from "react";
import { UserService } from "../../../services/api";
import useRefresh from "../../../hooks/useRefresh";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import useConfirm from "../../../hooks/useConfirm";
import QRButton from "../../organisms/QRButton";
import { useTranslation } from "react-i18next";
import useEnums from "../../../hooks/useEnums";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation } from "@tanstack/react-query";
import { useBusinessStore } from "../../../stores/Dummy";

export default function EditUserPage() {
  const { t } = useTranslation("User");
  const { roleDropDownItems } = useEnums();
  const { _id } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [extRef, setExtRef] = useState("");
  const [role, setRole] = useState<UserRole>("user");
  const [user, setUser] = useState<User | null>();
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [emailConfirmation, setEmailConfirmation] = useState(false);
  const miniCompanyList = useBusinessStore((state) => state.miniCompanyList);

  const confirm = useConfirm();

  const { mutate: loadUser, isPending } = useMutation({
    mutationFn: async () => {
      const filters: Filter[] = [
        { field: "_id", operator: "eq", type: "id", value: _id },
      ];
      const res = await UserService.getUserList({
        search: "",
        limit: 1,
        skip: 0,
        filters: JSON.stringify(filters),
      });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data?.docs?.length === 1) setUser(data.docs[0]);
    },
  });

  const handleDelete = () => {
    confirm(async () => {
      if (!_id) return;
      const res = await UserService.deleteUser({ id: _id });
      if (res?.data?.success) navigate(-1);
    });
  };

  const refresh = useRefresh(loadUser);

  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setCompany((user.company as unknown as string) ?? "");
    setPhone(user.phone);
    setRole(user.role);
    setEmailConfirmation(user.emailConfirmation);
    setOtpEnabled(user.otpEnabled);
    setExtRef(user.extRef);
  }, [user, miniCompanyList]);

  useEffect(() => {
    if (role !== "user") setOtpEnabled(true);
  }, [role]);

  useEffect(() => {
    loadUser();
  }, []);

  const handleCancel = () => {
    navigate("/m/user");
  };

  const handleSubmit = async () => {
    if (!_id) return;
    const res = await UserService.updateUser({
      id: _id,
      updateUserBody: {
        name,
        company,
        phone,
        password: newPassword,
        role,
        otpEnabled,
        emailConfirmation,
        extRef,
      },
    });
    if (res?.data?.success) navigate("/m/user");
  };

  const handleLoadQr = async () => {
    if (!_id) return undefined;
    const res = await UserService.getUserQR({ id: _id });
    return res?.data?.data;
  };

  return (
    <Paper sx={{ height: 1 }}>
      <FormTemplate
        title={`${t("Edit")} ${user?.name}`}
        onSubmit={handleSubmit}
        confirm
        onCancel={handleCancel}
        options={[
          {
            icon: <DeleteIcon color="error" />,
            label: t("Delete"),
            onClick: handleDelete,
          },
        ]}
      >
        <Stack spacing={2}>
          <FormInput
            loading={isPending}
            field="role"
            label={t("Role")}
            onChange={(v) => {
              setRole(v as UserRole);
            }}
            type="list"
            required
            value={role}
            list={roleDropDownItems}
          />
          <FormInput
            loading={isPending}
            field="email"
            label={t("Email")}
            onChange={() => {}}
            placeholder={t("EmailPlaceholder")}
            type="string"
            inputType="email"
            readOnly
            value={user?.email}
            required
          />
          <FormInput
            loading={isPending}
            field="username"
            label={t("Username")}
            readOnly
            value={user?.username}
            placeholder={t("Username")}
            required
            type="string"
            onChange={() => {}}
          />
          <FormInput
            loading={isPending}
            field="name"
            label={t("FullName")}
            value={name}
            placeholder={t("FullName")}
            required
            type="string"
            onChange={(v) => {
              if (typeof v === "string") setName(v);
            }}
          />
          <FormInput
            role="agent"
            field="company"
            label={t("Company")}
            placeholder={t("Company")}
            onChange={(v) => {
              setCompany(v as string);
            }}
            onSoloTextChange={(v) => console.log(v)}
            type="list"
            list={miniCompanyList.map?.((company) => ({
              name: `${company.name} (${company.regNo})`,
              value: company._id,
            }))}
            value={company}
            required
          />
          <FormInput
            loading={isPending}
            field="phone"
            label={t("Phone")}
            value={phone}
            placeholder={t("Phone")}
            type="string"
            onChange={(v) => {
              if (typeof v === "string") setPhone(v);
            }}
          />
          <FormInput
            loading={isPending}
            field="password"
            label={t("NewPassword")}
            value={newPassword}
            placeholder={t("NewPassword")}
            type="string"
            onChange={(v) => {
              if (typeof v === "string") setNewPassword(v);
            }}
          />
          <FormInput
            loading={isPending}
            field="extRef"
            label={t("ExtRef")}
            value={extRef}
            placeholder={t("ExtRefPlaceholder")}
            type="string"
            onChange={(v) => setExtRef(v as string)}
          />
          <FormInput
            loading={isPending}
            field="emailConfirmation"
            value={emailConfirmation}
            onChange={(x) => setEmailConfirmation(x as boolean)}
            label={t("EmailConfirmation")}
            type="boolean"
            required
          />
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <FormInput
              loading={isPending}
              field="otpEnabled"
              value={otpEnabled}
              onChange={(x) => setOtpEnabled(x as boolean)}
              label={t("OTP")}
              type="boolean"
              disabled={user?.role !== "user"}
              required
            />
            <QRButton
              buttonProps={{ sx: { height: 55 } }}
              srcFunction={handleLoadQr}
              disabled={!otpEnabled || isPending}
            />
          </Stack>
        </Stack>
      </FormTemplate>
    </Paper>
  );
}
